import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Send, Loader2, CalendarCheck, Home as HomeIcon, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { IUBENDA_PRIVACY_URL } from "@/lib/iubenda";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { modalita: "In sede da noi", nome_cognome: "", telefono: "", email: "", indirizzo: "", messaggio: "", domicilioConsent: false, privacy: false, website: "" };

const COST_TEXT =
  "Il servizio di misurazione a domicilio prevede un costo di uscita del tecnico, dovuto indipendentemente dall'eventuale acquisto successivo dei prodotti. L'importo viene comunicato al cliente prima della conferma dell'appuntamento.";
const CONSENT_LABEL =
  "Dichiaro di aver compreso che l'uscita del tecnico a domicilio è a pagamento anche in caso di mancato acquisto.";

const MODES = [
  { id: "In sede da noi", label: "In sede da noi", icon: HomeIcon },
  { id: "A domicilio", label: "A domicilio", icon: MapPin },
];

const QuickBooking = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const atHome = form.modalita === "A domicilio";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_cognome || !form.telefono) {
      toast.error("Inserisci nome e cellulare per prenotare.");
      return;
    }
    if (atHome && !form.indirizzo) {
      toast.error("Indica l'indirizzo per il servizio a domicilio.");
      return;
    }
    if (atHome && !form.domicilioConsent) {
      toast.error("Conferma di aver compreso che l'uscita del tecnico a domicilio è a pagamento.");
      return;
    }
    if (!form.privacy) {
      toast.error("Devi accettare la Privacy Policy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        nome_cognome: form.nome_cognome,
        telefono: form.telefono,
        email: form.email || null,
        motivo: `Misurazione — ${form.modalita}`,
        messaggio: atHome
          ? `${form.messaggio || ""}\n[Il cliente ha compreso e accettato che l'uscita del tecnico a domicilio è a pagamento, anche in caso di mancato acquisto.]`.trim()
          : form.messaggio || "",
        modalita: form.modalita,
        indirizzo: atHome ? form.indirizzo : null,
        privacy_accettata: form.privacy,
        origine: "Prenota misurazione",
        pagina: window.location.pathname,
        website: form.website,
      });
      toast.success(
        atHome
          ? "Richiesta inviata! Ti ricontatteremo per confermare l'appuntamento e comunicarti il costo di uscita del tecnico."
          : "Richiesta inviata! Ti ricontatteremo per fissare la misurazione."
      );
      setForm(initialForm);
    } catch {
      toast.error("Si è verificato un errore. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="prenota" className="scroll-mt-28 grid lg:grid-cols-2 gap-12 items-center rounded-[2rem] border border-[#E7EDF2] bg-white p-8 lg:p-12 shadow-[0_30px_70px_-40px_rgba(15,25,35,0.25)]">
      <div>
        <span className="eyebrow"><CalendarCheck size={14} /> Prenotazione rapida</span>
        <h3 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">Prenota la tua misurazione.</h3>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Scegli se venire in sede o farti raggiungere a domicilio: lascia i tuoi contatti e ti richiamiamo per fissare l'appuntamento. Nessun impegno, solo la misura giusta.
        </p>
        <ul className="mt-6 space-y-3">
          {["In sede o a domicilio", "Personale qualificato", "Prova della calza inclusa"].map((b) => (
            <li key={b} className="flex items-center gap-3 text-navy/80 text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green/12 text-green"><Check size={12} strokeWidth={3} /></span>
              {b}
            </li>
          ))}
        </ul>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="quickbooking-form">
        {/* Modalità: In sede / A domicilio */}
        <div>
          <Label className="text-navy">Dove preferisci?</Label>
          <div className="mt-2 grid grid-cols-2 gap-3" data-testid="qb-modalita">
            {MODES.map((m) => {
              const active = form.modalita === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`qb-mode-${m.id === "A domicilio" ? "domicilio" : "sede"}`}
                  onClick={() => setForm((f) => ({ ...f, modalita: m.id }))}
                  className={`flex items-center justify-center gap-2 rounded-xl border px-4 h-12 text-sm font-semibold transition-colors ${active ? "border-green bg-green/10 text-green-deep" : "border-[#E7EDF2] text-navy/70 hover:border-green/50"}`}
                >
                  <m.icon size={16} /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label htmlFor="qb-nome" className="text-navy">Nome e Cognome</Label>
          <Input id="qb-nome" data-testid="qb-nome" value={form.nome_cognome} onChange={set("nome_cognome")} placeholder="Mario Rossi" className="mt-2 h-12" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="qb-tel" className="text-navy">Cellulare</Label>
            <Input id="qb-tel" data-testid="qb-telefono" value={form.telefono} onChange={set("telefono")} placeholder="339 1424138" className="mt-2 h-12" />
          </div>
          <div>
            <Label htmlFor="qb-email" className="text-navy">Email <span className="text-muted-foreground font-normal">(facoltativa)</span></Label>
            <Input id="qb-email" type="email" data-testid="qb-email" value={form.email} onChange={set("email")} placeholder="nome@email.it" className="mt-2 h-12" />
          </div>
        </div>

        {atHome && (
          <div data-testid="qb-indirizzo-wrap">
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="qb-cost-notice">
              <MapPin size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <span>{COST_TEXT}</span>
            </div>
            <Label htmlFor="qb-indirizzo" className="text-navy">Indirizzo (dove raggiungerti)</Label>
            <Input id="qb-indirizzo" data-testid="qb-indirizzo" value={form.indirizzo} onChange={set("indirizzo")} placeholder="Via, numero civico, città" className="mt-2 h-12" />
            <label className="mt-4 flex items-start gap-3 text-sm text-navy/80 cursor-pointer" data-testid="qb-domicilio-consent-wrap">
              <input
                type="checkbox"
                data-testid="qb-domicilio-consent"
                checked={form.domicilioConsent}
                onChange={(e) => setForm((f) => ({ ...f, domicilioConsent: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 accent-green"
              />
              <span>{CONSENT_LABEL}</span>
            </label>
          </div>
        )}

        <div>
          <Label htmlFor="qb-msg" className="text-navy">Note (facoltative)</Label>
          <Textarea id="qb-msg" data-testid="qb-messaggio" value={form.messaggio} onChange={set("messaggio")} placeholder="Es. disponibilità, preferenze orario…" rows={3} className="mt-2 resize-none" />
        </div>
        {/* Honeypot anti-spam: invisibile agli utenti */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden="true" className="hidden" />
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="qb-health-notice">
          Non inserire diagnosi, patologie, prescrizioni, referti o altre informazioni relative alla salute. Il modulo è destinato esclusivamente alla richiesta di contatto e alla prenotazione del servizio.
        </p>
        <div className="flex items-start gap-3">
          <Checkbox
            id="qb-privacy"
            checked={form.privacy}
            onCheckedChange={(v) => setForm((f) => ({ ...f, privacy: v === true }))}
            data-testid="qb-privacy-checkbox"
            className="mt-0.5 border-navy/30 data-[state=checked]:bg-green data-[state=checked]:border-green"
          />
          <Label htmlFor="qb-privacy" className="text-sm font-normal leading-relaxed text-navy/80 cursor-pointer">
            Dichiaro di aver letto l&apos;{" "}
            <a href={IUBENDA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-green underline underline-offset-2" data-testid="qb-privacy-link">Informativa Privacy</a>.
          </Label>
        </div>
        <button type="submit" data-testid="quickbooking-submit" disabled={loading || !form.privacy} className="btn-green w-full disabled:opacity-60 disabled:cursor-not-allowed">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Invio…</> : <>Prenota misurazione <Send size={16} /></>}
        </button>
      </form>
    </div>
  );
};

export default QuickBooking;
